// @flow

import './DesktopHeader.scss';

import { BOILogoNoSpace, Typeahead } from '@boi/core/lib';
import {
  faLocationArrow,
  faSignInAlt,
  faSignOutAlt,
  faUser,
  faUserAlt
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, useHistory, useLocation, withRouter } from 'react-router-dom';

import { CognitoContext } from '../../CognitoUtils';
import { darkBlue, greyDark, whiteColor } from '../../constants/colors';
import { USER_BRANCH } from '../../constants/sessionStorage/userStorageIdentifiers';
import {
  dashboardLinkEnabled,
  getItemFromSessionStorage,
  isAgent,
  onClickIfEnabled
} from '../../helpers';
import { getAgentName } from '../../helpers/AgentCheck';
import { routes } from '../../routes';
import { updateUserProfileBranch } from '../../services/profile/profile';
import type { HtmlRef } from '../../types';
import UserContext from '../../UserContext';
import PopOverWrapper from '../PopOverWrapper/PopOverWrapper';
import { SkipMenuTabbing } from './SkipMenuTabbing';
import { SubHeading } from './SubHeading';

type Props = { mainContentRef: HtmlRef };

const DesktopHeader = ({ mainContentRef }: Props) => {
  const className = 'c-DesktopHeader';
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userPopOverVisible, setUserPopOverVisible] = useState(false);
  const [branchPopOverVisible, setBranchPopOverVisible] = useState(false);
  const { cognitoUser, branchList } = useContext(CognitoContext);
  const { user, userDispatch, agentScriptPending } = useContext(UserContext);

  const history = useHistory();
  const location = useLocation();
  const ref: HtmlRef = useRef(null);

  const getAccessUrl = () => {
    return isLoggedIn ? (
      popOverDropDown()
    ) : !isAgent() ? (
      loggedInAndSignIn()
    ) : (
      <></>
    );
  };

  const titleHeader = () => {
    const expandOnTab = (e: SyntheticKeyboardEvent<HTMLInputElement>) => {
      if (['Enter', 'ArrowDown', 'Tab'].includes(e.key)) {
        setUserPopOverVisible(true);
      }
    };

    return (
      <>
        {isLoggedIn && (
          <>
            <span
              className={`${className}__loggedInLabel`}
              tabIndex={0}
              onKeyDown={expandOnTab}
            >
              {cognitoUser?.attributes?.given_name || getAgentName(cognitoUser)}
            </span>
            <FontAwesomeIcon
              className={'faIconHover'}
              icon={faUser}
              size="1x"
              color={greyDark}
              alt="User"
            />
          </>
        )}
      </>
    );
  };

  const branchHeader = () => {
    return (
      <>
        {isLoggedIn ? (
          <>
            <span
              data-test-id="branch-name"
              className={`${className}__loggedInLabel`}
            >
              {user?.branchName.branch_name || 'Search Branch'}
            </span>
            <FontAwesomeIcon
              className={'faIconHover'}
              icon={faLocationArrow}
              size="1x"
              color={greyDark}
              alt="location"
            />
          </>
        ) : null}
      </>
    );
  };

  const loggedInAndSignIn = () => {
    return (
      <div className={`${className}__desktopAuthMenus`}>
        {location.pathname === '/login' ? (
          <div className={`${className}__logInPageLabel`}>
            Don&lsquo;t have an account?
          </div>
        ) : (
          <Link to={routes.loginPage.url} className={`${className}__logInLink`}>
            <div className={`${className}__logInLabel`}>
              Login
              <span>
                <FontAwesomeIcon
                  className={'loginIcon'}
                  icon={faSignInAlt}
                  size="1x"
                  color={greyDark}
                  alt="Sign in"
                />
              </span>
            </div>
          </Link>
        )}
        <Link to={routes.signUp.url} className={`${className}__signUpLink`}>
          <div className={`${className}__SignUpLabel`}>
            Sign Up
            <span>
              <FontAwesomeIcon
                className={'signupIcon'}
                icon={faUserAlt}
                size="1x"
                color={whiteColor}
                alt="Signup icon"
              />
            </span>
          </div>
        </Link>
      </div>
    );
  };

  const popOverDropDown = () => (
    <>
      {isAgent() && (
        <div className={`${className}__isAgentLocationAndNameHeader`}>
          <PopOverWrapper
            clickTitle={branchHeader()}
            visibility={branchPopOverVisible}
            updateVisibility={setBranchPopOverVisible}
          >
            <div>
              <Typeahead
                data={branchList}
                placeholder="Search Branch Type"
                onChange={(selectedItem: any) => {
                  if (selectedItem) {
                    updateUserProfileBranch({
                      branchName: selectedItem
                    }).then((response: any) => {
                      if (response.branch_name) {
                        userDispatch({
                          type: 'setBranchName',
                          payload: { branchName: response }
                        });
                      }
                      setBranchPopOverVisible(false);
                    });
                  }
                }}
              />
            </div>
          </PopOverWrapper>
          <PopOverWrapper
            clickTitle={titleHeader()}
            visibility={userPopOverVisible}
            updateVisibility={setUserPopOverVisible}
          >
            <Link to={routes.logoutPage.url}>
              <div className={`${className}__logoutLabel`}>
                Logout
                <span className={`${className}__logoutLabelIcon`}>
                  <FontAwesomeIcon
                    className={`${className}__logoutIcon`}
                    icon={faSignOutAlt}
                    size="1x"
                    color={greyDark}
                  />
                </span>
              </div>
            </Link>
          </PopOverWrapper>
        </div>
      )}
      {!isAgent() && (
        <>
          <PopOverWrapper
            clickTitle={titleHeader()}
            visibility={userPopOverVisible}
            updateVisibility={setUserPopOverVisible}
          >
            <Link to={routes.logoutPage.url}>
              <div className={`${className}__logoutLabel`}>
                Logout
                <span className={`${className}__logoutLabelIcon`}>
                  <FontAwesomeIcon
                    icon={faSignOutAlt}
                    size="1x"
                    color={greyDark}
                  />
                </span>
              </div>
            </Link>
          </PopOverWrapper>
        </>
      )}
    </>
  );

  useEffect(() => {
    const branch = JSON.parse(getItemFromSessionStorage(USER_BRANCH));
    if (
      !(user?.branchName && Object.keys(user?.branchName).length > 0) &&
      branch
    ) {
      const userBranch = branch;
      userDispatch({
        type: 'setBranchName',
        payload: { branchName: userBranch }
      });
    }
  }, [user]);

  useEffect(() => {
    setIsLoggedIn(!!cognitoUser);
  }, [cognitoUser]);

  return (
    <>
      <div className={`${className}`} ref={ref} tabIndex={-1}>
        <SkipMenuTabbing mainContentRef={mainContentRef} parentRef={ref} />
        <div
          className={`${className}__logo`}
          onClick={onClickIfEnabled(
            dashboardLinkEnabled(agentScriptPending, location),
            () => history.push(routes.wallet.url)
          )}
          tabIndex={0}
        >
          <BOILogoNoSpace color={darkBlue} />
        </div>
        <SubHeading
          isLoggedIn={isLoggedIn}
          agentScriptPending={agentScriptPending}
        />
        {getAccessUrl()}
      </div>
    </>
  );
};

export default withRouter(DesktopHeader);
